/**
 * Chapters for a set of subjects, ready for the plan editor.
 *
 * Shared by every surface that authors plans — a class's tab and the platform's —
 * because they need the identical thing and differ only in how they arrive at
 * the subject list. A second copy is where the two drift: the chapter-dates
 * screen opens per subject and asks for its chapters synchronously, so they must
 * be fetched up front, and getting that wrong on one surface and not the other
 * is invisible until someone opens a plan and finds no chapters in it.
 *
 * Each query is cached by curriculum id, so a syllabus already read by another
 * tab costs nothing here.
 */
import { useCallback } from 'react';
import { useQueries } from '@tanstack/react-query';
import { curriculumApi } from '../../api/curriculumApi';

export interface PlannableSubject {
  subject: string;
  curriculumId?: string;
}

export function useChaptersFor(availableSubjects: PlannableSubject[]) {
  const placementQueries = useQueries({
    queries: availableSubjects.map(x => ({
      queryKey: ['curriculum-placements', x.curriculumId],
      queryFn: () => curriculumApi.getPlacements(x.curriculumId!),
      enabled: !!x.curriculumId,
    })),
  });

  return useCallback((sub: string) => {
    const at = availableSubjects.findIndex(x => x.subject === sub);
    if (at < 0) return [];
    return (placementQueries[at]?.data?.placements ?? [])
      .filter(p => p.bookLevel === 'chapter')
      .map(p => ({ id: p.id, number: p.number, name: p.displayName }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableSubjects, placementQueries.map(q => q.dataUpdatedAt).join(',')]);
}
